// src/context/JobContext.js

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useData } from './DataContext'; // Add this import
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth'; // <-- FIX: Import getAuth
import { jobReducer, actionTypes, initialJobState } from './jobReducer';

// --- API Imports: Only job-related functions are needed here ---
import {
  startDiscoverySearchJob as apiStartDiscoverySearchJob,
  checkDiscoveryJobStatus as apiCheckDiscoveryJobStatus,
  cancelDiscoveryJob as apiCancelDiscoveryJob,
} from '../api/DiscoveryPageApi';
import {
  startContentAnalysisJob as apiStartContentAnalysisJob,
  checkContentAnalysisJobStatus as apiCheckContentAnalysisJobStatus,
  cancelContentAnalysisJob as apiCancelContentAnalysisJob,
} from '../api/PublicationPageApi';
import {
  startAgentJob as apiStartAgentJob,
  fetchAgentJobHistory as apiFetchAgentJobHistory,
  fetchAgentJobDetails as apiFetchAgentJobDetails,
  cancelAgentJob as apiCancelAgentJob, // Import new API
  deleteAgentJob as apiDeleteAgentJob, // Import new API
  sendAgentChatMessage as apiSendAgentChatMessage,
  fetchAgentChats as apiFetchAgentChats,
  fetchAgentChatHistory as apiFetchAgentChatHistory, // Import the new API
} from '../api/AiAgentApi'; // This file will be created later
import { apiDispatchInvoices } from '../api/SuperAdminApi';

import { useAuth } from './AuthContext';

// --- Context Definition ---
const JobContext = createContext();

// --- Provider Component ---
export const JobProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    jobReducer,
    initialJobState,
    // Initializer to rehydrate state from localStorage
    (initial) => {
      try {
        const storedJobs = localStorage.getItem('jobState');
        if (storedJobs) {
          const parsedJobs = JSON.parse(storedJobs);
          const finalState = { ...initial };

          // Rehydrate Discovery Job
          if (parsedJobs.discoveryJob) {
            if (parsedJobs.discoveryJob.jobStatus === 'failed') {
              finalState.discoveryJob = initial.discoveryJob;
            } else {
              finalState.discoveryJob = {
                ...initial.discoveryJob,
                ...parsedJobs.discoveryJob,
              };
            }
          }

          // Rehydrate Content Analysis Job
          if (parsedJobs.contentAnalysisJob) {
            if (
              parsedJobs.contentAnalysisJob.jobStatus === 'failed' ||
              parsedJobs.contentAnalysisJob.jobStatus === 'finished'
            ) {
              finalState.contentAnalysisJob = initial.contentAnalysisJob;
            } else {
              finalState.contentAnalysisJob = {
                ...initial.contentAnalysisJob,
                ...parsedJobs.contentAnalysisJob,
              };
            }
          }
          return finalState;
        }
        return initial;
      } catch (e) {
        console.warn('Failed to parse jobState from localStorage:', e);
        localStorage.removeItem('jobState');
        return initial;
      }
    },
  );

  console.log('[JobProvider] Current state:', state);
  const { setError: setGlobalError } = useData(); // 1. Hook into Global Error state

  // Sync state back to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('jobState', JSON.stringify(state));
  }, [state]);

  const { currentUser } = useAuth();

  const { pathname } = useLocation();

  // Destructure for cleaner dependency arrays
  const {
    discoveryJob,
    contentAnalysisJob,
    agentJobHistory,
    selectedJobDetails,
    selectedJobType,
    isJobDetailsModalOpen,
    agentChatsList, // <--- NEW
    activeAgentChatId,
  } = state;

  // --- Dispatcher Callbacks ---
  const setDiscoveryJobState = useCallback(
    (payload) => {
      dispatch({ type: actionTypes.SET_DISCOVERY_JOB_STATE, payload });
    },
    [dispatch],
  );

  const clearDiscoveryJobState = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_DISCOVERY_JOB_STATE });
  }, [dispatch]);

  const clearDiscoveryJobError = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_DISCOVERY_JOB_ERROR });
  }, [dispatch]);

  const setContentAnalysisJobState = useCallback(
    (payload) => {
      dispatch({ type: actionTypes.SET_CONTENT_ANALYSIS_JOB_STATE, payload });
    },
    [dispatch],
  );

  const clearContentAnalysisJobState = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_CONTENT_ANALYSIS_JOB_STATE });
  }, [dispatch]);

  const clearContentAnalysisJobError = useCallback(() => {
    dispatch({ type: actionTypes.CLEAR_CONTENT_ANALYSIS_JOB_ERROR });
  }, [dispatch]);

  // --- NEW DISPATCHERS FOR UI UPDATES ---
  const updateJobResultsAfterImport = useCallback(
    (platformIdsToRemove) => {
      dispatch({
        type: actionTypes.UPDATE_JOB_RESULTS_AFTER_IMPORT,
        payload: platformIdsToRemove,
      });
    },
    [dispatch],
  );

  const updateJobResultsAfterMoveToHold = useCallback(
    (newOnHoldRecords) => {
      dispatch({
        type: actionTypes.UPDATE_JOB_RESULTS_AFTER_MOVE_TO_HOLD,
        payload: newOnHoldRecords,
      });
    },
    [dispatch],
  );

  const updateJobResultsAfterDiscard = useCallback(
    (newDiscardedRecords) => {
      dispatch({
        type: actionTypes.UPDATE_JOB_RESULTS_AFTER_DISCARD,
        payload: newDiscardedRecords,
      });
    },
    [dispatch],
  );

  // --- NEW: AI Agent Dispatchers ---
  const setAgentJobHistory = useCallback(
    (jobs) => {
      dispatch({ type: actionTypes.SET_AGENT_JOB_HISTORY, payload: jobs });
    },
    [dispatch],
  );

  const setSelectedJobDetails = useCallback(
    (payload) => {
      dispatch({ type: actionTypes.SET_SELECTED_JOB_DETAILS, payload });
    },
    [dispatch],
  );

  // --- NEW: Agent Chat Dispatchers ---

  const getAgentChats = useCallback(async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken(true);
      const result = await apiFetchAgentChats(token);
      if (result.status) {
        dispatch({
          type: actionTypes.SET_AGENT_CHATS_LIST,
          payload: result.data,
        });
      }
    } catch (e) {
      console.error('Failed to fetch agent chats:', e);
    }
  }, [currentUser, dispatch]);

  const setActiveAgentChat = useCallback(
    (chatId) => {
      dispatch({ type: actionTypes.SET_ACTIVE_AGENT_CHAT_ID, payload: chatId });
    },
    [dispatch],
  );

  const addAgentChatToList = useCallback(
    (chatSummary) => {
      dispatch({
        type: actionTypes.ADD_AGENT_CHAT_TO_LIST,
        payload: chatSummary,
      });
    },
    [dispatch],
  );

  // --- API Wrapper Callbacks ---
  const startDiscoverySearchJob = useCallback(
    async (searchParams) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return { status: false, message: 'User not authenticated.' };

      // STEP 1: Get a fresh, guaranteed valid token
      const token = await user.getIdToken(true);

      const result = await apiStartDiscoverySearchJob(token, searchParams);
      if (result.status && result.data?.jobId) {
        setDiscoveryJobState({
          jobId: result.data.jobId,
          jobStatus: 'pending',
          jobStartTime: Date.now(),
          results: null,
          error: null,
        });
      }
      return result;
    },
    [setDiscoveryJobState],
  );

  const startContentAnalysisJob = useCallback(
    async (influencerId, publicationsToAnalyze, forceReanalysis) => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return { status: false, message: 'User not authenticated.' };

      const token = await user.getIdToken(true);
      const result = await apiStartContentAnalysisJob(
        token,
        influencerId,
        publicationsToAnalyze,
        forceReanalysis,
      );
      if (result.status && result.data?.jobId) {
        setContentAnalysisJobState({
          jobId: result.data.jobId,
          influencerId: influencerId,
          jobStatus: 'pending',
          message: 'Job submitted. Waiting for progress...',
        });
      }
      return result;
    },
    [setContentAnalysisJobState],
  );

  const checkContentAnalysisJobStatus = useCallback(
    async (jobId) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        return await apiCheckContentAnalysisJobStatus(token, jobId);
      } catch (error) {
        return {
          status: false,
          message: error.message || 'Client-side error.',
        };
      }
    },
    [currentUser],
  );

  const checkDiscoveryJobStatus = useCallback(
    async (jobId) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        return await apiCheckDiscoveryJobStatus(token, jobId);
      } catch (error) {
        return {
          status: false,
          message: error.message || 'An unexpected client-side error occurred.',
        };
      }
    },
    [currentUser],
  );

  const cancelDiscoveryJob = useCallback(async () => {
    // 1. ALWAYS Reset the UI immediately (Force Stop)
    setDiscoveryJobState({
      jobStatus: 'idle',
      jobId: null,
      message: null,
      error: null,
    });

    // 2. If we lack the data to call the backend, just return now.
    // The UI is already fixed, which is the priority for the user.
    if (!discoveryJob.jobId || !currentUser) {
      console.warn(
        'Resetting UI only. No Job ID or User found to cancel on backend.',
      );
      return;
    }

    const idToCancel = discoveryJob.jobId;

    try {
      toast.info('Canceling search...');
      // 3. Call Backend API
      const token = await currentUser.getIdToken(true);
      await apiCancelDiscoveryJob(token, idToCancel);
    } catch (e) {
      console.error('Error canceling discovery job:', e);
      // Do NOT revert the UI state here. If the backend fails to cancel,
      // we still want the user to be able to try a new search.
      toast.error(
        'Failed to cancel job on server, but search has been stopped locally.',
      );
    }
  }, [currentUser, discoveryJob.jobId, setDiscoveryJobState]);

  const cancelContentAnalysisJob = useCallback(async () => {
    // 1. ALWAYS Reset the UI immediately (Force Stop)
    // This kills the spinner and clears the "stuck" state in localStorage
    setContentAnalysisJobState({
      jobStatus: 'idle',
      jobId: null,
      message: null,
      error: null,
    });

    // 2. Check if we actually have a Job ID to cancel on the backend.
    // If not (e.g., state was corrupted), we stop here. The UI is already fixed.
    if (!contentAnalysisJob.jobId || !currentUser) {
      console.warn(
        'Resetting UI only. No Job ID or User found to cancel on backend.',
      );
      return;
    }

    const idToCancel = contentAnalysisJob.jobId;

    try {
      toast.info('Canceling analysis...');

      // 3. Call Backend API
      const token = await currentUser.getIdToken(true);
      await apiCancelContentAnalysisJob(token, idToCancel);
    } catch (e) {
      console.error('Error canceling content analysis job:', e);
      // Do NOT revert the UI state here. The priority is keeping the app usable.
      toast.error(
        'Failed to cancel job on server, but analysis has been stopped locally.',
      );
    }
  }, [currentUser, contentAnalysisJob.jobId, setContentAnalysisJobState]);

  const fetchAgentJobHistory = useCallback(async () => {
    if (!currentUser)
      return { status: false, message: 'User not authenticated.' };
    try {
      const token = await currentUser.getIdToken(true);
      const result = await apiFetchAgentJobHistory(token);
      if (result.status) {
        setAgentJobHistory(result.data);
      } else {
        // Don't show a toast for a failed poll, just log it.
        console.error('Failed to refresh job history:', result.message);
      }
      return result;
    } catch (e) {
      console.error(
        'A client-side error occurred while fetching job history.',
        e,
      );
      return { status: false, message: e.message };
    }
  }, [currentUser, setAgentJobHistory]);

  // --- NEW: AI Agent API Wrappers ---
  const startAgentJob = useCallback(
    async (payload) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiStartAgentJob(token, payload);
        if (result.status) {
          toast.success(
            `Job "${payload.jobName}" has been successfully queued!`,
          );
          // Trigger an immediate refresh of the job history to show the new 'queued' job
          fetchAgentJobHistory();
        } else {
          toast.error(result.message || 'Failed to start agent job.');
        }
        return result;
      } catch (e) {
        toast.error('A client-side error occurred while starting the job.');
        return { status: false, message: e.message };
      }
    },
    [currentUser, fetchAgentJobHistory],
  );

  const fetchAgentJobDetails = useCallback(
    async (jobId, jobType) => {
      if (!currentUser)
        return { status: false, message: 'User not authenticated.' };
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiFetchAgentJobDetails(token, jobId);

        if (result.status) {
          // REUSE THE DISPATCHER with the composite object
          setSelectedJobDetails({
            details: result.data,
            jobType: jobType,
          });
        } else {
          toast.error(result.message || 'Failed to fetch job details.');
        }
        return result;
      } catch (e) {
        console.error('Error fetching agent job details:', e);
        toast.error('Error loading details.');
        return { status: false, message: e.message };
      }
    },
    [currentUser, setSelectedJobDetails],
  ); // <--- Depend on the dispatcher

  const cancelAgentJob = useCallback(
    async (jobId) => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiCancelAgentJob(token, jobId);

        if (result.status) {
          toast.success('Job canceled.');
          // Optimistic Update: Mark as canceled immediately
          dispatch({
            type: actionTypes.UPDATE_AGENT_JOB_STATUS,
            payload: {
              id: jobId,
              status: 'canceled',
              statusMessage: 'Stopped by user',
            },
          });
          // Note: The next polling cycle will also fetch this, but optimistic makes it feel snappy.
        } else {
          toast.error(result.message || 'Failed to cancel job.');
        }
      } catch (e) {
        console.error(e);
        toast.error('Error canceling job.');
      }
    },
    [currentUser, dispatch],
  );

  const deleteAgentJob = useCallback(
    async (jobId) => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiDeleteAgentJob(token, jobId);

        if (result.status) {
          toast.success('Job record deleted.');
          // Optimistic Update: Remove from list immediately
          dispatch({
            type: actionTypes.REMOVE_AGENT_JOB,
            payload: jobId,
          });
        } else {
          toast.error(result.message || 'Failed to delete job.');
        }
      } catch (e) {
        console.error(e);
        toast.error('Error deleting job.');
      }
    },
    [currentUser, dispatch],
  );

  const sendAgentChat = useCallback(
    async (payload, signal) => {
      if (!currentUser) {
        toast.error('You must be logged in to chat.');
        return null;
      }
      try {
        // Get fresh token
        const token = await currentUser.getIdToken();
        // Pass the signal (AbortController) to the API
        const response = await apiSendAgentChatMessage(token, payload, signal);
        return response;
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error('Chat Error:', e);
          toast.error('Failed to connect to Agent.');
        }
        return null;
      }
    },
    [currentUser],
  );

  // Wrapper to fetch history (used by the ChatWindow)
  const getAgentChatHistory = useCallback(
    async (chatId) => {
      if (!currentUser) return null;
      try {
        const token = await currentUser.getIdToken(true);
        const result = await apiFetchAgentChatHistory(token, chatId);
        if (result.status) {
          return result.data; // { chatId, title, messages: [] }
        }
        return null;
      } catch (e) {
        console.error('Failed to fetch chat history:', e);
        return null;
      }
    },
    [currentUser],
  );

  const dispatchMonthlyEmails = useCallback(
    async (billingMonth) => {
      if (!currentUser) return;

      try {
        const token = await currentUser.getIdToken(true);
        // Explicitly pass undefined if billingMonth is an object (event)
        const passedMonth =
          typeof billingMonth === 'string' ? billingMonth : undefined;

        const result = await apiDispatchInvoices(token, passedMonth);

        if (result.status) {
          toast.info('Dispatch process started...');
          await fetchAgentJobHistory();
        }
        return result;
      } catch (e) {
        toast.error('A client-side error occurred.');
        return { status: false, message: e.message };
      }
    },
    [currentUser, fetchAgentJobHistory],
  );

  // --- NEW: GLOBAL POLLING useEffect for Discovery Page ---

  useEffect(() => {
    // 1. GUARD CLAUSE: The poller should only be active when a job is in the 'pending' state and has a valid job ID.
    //    This prevents the effect from running unnecessarily.
    if (discoveryJob.jobStatus !== 'pending' || !discoveryJob.jobId) {
      return;
    }

    console.log(
      `[DataContext Poller] A pending job ('${discoveryJob.jobId}') was detected. Starting resilient polling.`,
    );
    const JOB_TIMEOUT_MS = 19 * 60 * 1000;

    const intervalId = setInterval(async () => {
      // A) Check if the user has logged out in another tab while the job was running.
      if (!currentUser) {
        console.warn(
          '[DataContext Poller] User session ended during polling. Halting job.',
        );
        setDiscoveryJobState({
          jobStatus: 'failed',
          error: 'Your session ended.',
          jobId: null,
        });
        return;
      }

      // B) Check against our client-side safety timeout.
      if (Date.now() - discoveryJob.jobStartTime > JOB_TIMEOUT_MS) {
        const timeoutMessage =
          'The discovery search took too long and timed out.';
        console.error(
          `[DataContext Poller - Discovery] Job timed out after ${JOB_TIMEOUT_MS / 60000} minutes.`,
        );
        toast.error(timeoutMessage);
        setDiscoveryJobState({
          jobStatus: 'failed',
          error: timeoutMessage,
          jobId: null,
        });
        return;
      }

      // --- RESILIENT API CALL LOGIC ---
      try {
        let result = await checkDiscoveryJobStatus(discoveryJob.jobId);

        // ** RETRY LOGIC **
        // Check if the first call failed specifically with an auth error
        const isAuthError =
          result.statusCode === 401 ||
          result.statusCode === 403 ||
          result.error === 'HTTP_ERROR_401' ||
          result.error === 'HTTP_ERROR_403';

        if (!result.status && isAuthError) {
          console.warn(
            '[JobContext Poller - Discovery] Auth error detected. Forcing token refresh and retrying...',
          );
          const auth = getAuth();
          if (auth.currentUser) {
            await auth.currentUser.getIdToken(true);
            result = await checkDiscoveryJobStatus(discoveryJob.jobId);
          }
        }
        // Now, process the final result (either from the first success or the retry)
        if (result && result.status && result.data) {
          const backendJob = result.data;
          switch (backendJob.status) {
            case 'finished':
              console.log('[DataContext Poller] Job finished successfully.');
              toast.success('Discovery search complete!');
              setDiscoveryJobState({
                jobStatus: 'finished',
                results: backendJob.data,
                error: null,
                jobId: null,
              });
              break;

            case 'failed':
            case 'error':
              // The backend job ITSELF failed. This is a legitimate failure.
              console.error(
                "[JobContext Poller - Discovery] Job reported as 'failed' by the backend:",
                backendJob,
              );
              const searchErrorCode =
                backendJob.error ||
                backendJob.data?.code ||
                backendJob.data?.error;
              const searchErrorObj = {
                message:
                  backendJob.message ||
                  'The search process failed unexpectedly.',
                code: searchErrorCode,
                data: backendJob.data || null,
              };

              // FIX: Call the CORRECT state setter
              setDiscoveryJobState({
                jobStatus: 'failed',
                error: searchErrorObj,
                jobId: null,
              });

              // PROMOTE TO GLOBAL MODAL
              if (
                ['OAUTH_TOKEN_REVOKED', 'OAUTH_NOT_CONFIGURED'].includes(
                  searchErrorCode,
                )
              ) {
                setGlobalError(searchErrorObj);
              } else {
                toast.error('The discovery search completed with errors.');
              }
              break;

            case 'pending':
              // This is the most common in-progress status we expect.
              setDiscoveryJobState({
                jobStatus: 'pending',
                message: backendJob.message,
              });
              break;

            default:
              // This catches any other non-final status ('queued', 'started', or future ones).
              // We will treat it just like 'pending' to keep the UI active.
              console.warn(
                `[DataContext Poller] Received unknown job status: '${backendJob.status}'. Treating as pending.`,
              );
              setDiscoveryJobState({
                jobStatus: 'pending', // Keep the UI in the pending state
                message: backendJob.message || 'Processing...', // Use the message if available
              });
              break;
          }
        } else {
          const communicationErrorObject = {
            message:
              result.message ||
              'Lost connection during analysis. The job may still be running.',
            code: result.error || 'COMMUNICATION_ERROR', // Use a generic code
            data: null,
          };
          console.error(
            '[DataContext Poller] Persistent communication failure. Failing job with error:',
            communicationErrorObject,
          );
          toast.error(communicationErrorObject.message);

          // Pass the structured OBJECT to the state, not a string.
          setDiscoveryJobState({
            jobStatus: 'failed',
            error: communicationErrorObject,
            jobId: null,
          });
          // --- END OF REFACTORED BLOCK ---
        }
      } catch (e) {
        const criticalError = {
          message:
            'A critical client-side error occurred while polling for results.',
          code: 'CRITICAL_POLLER_FAILURE',
          data: null,
        };
        console.error(`[DataContext Poller] Critical exception: ${e.message}`);
        toast.error(criticalError.message);
        setDiscoveryJobState({
          jobStatus: 'failed',
          error: criticalError,
          jobId: null,
        });
        // --- END OF REFACTORED BLOCK ---
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, [
    discoveryJob.jobId,
    discoveryJob.jobStatus,
    discoveryJob.jobStartTime,
    checkDiscoveryJobStatus,
    setDiscoveryJobState,
    currentUser, // Re-run if user changes
    setGlobalError,
    // Removed API functions from deps, they are stable via useCallback
  ]);

  // --- NEW: GLOBAL POLLING useEffect for Publication Page ---
  useEffect(() => {
    if (
      contentAnalysisJob.jobStatus !== 'pending' ||
      !contentAnalysisJob.jobId
    ) {
      return;
    }

    console.log(
      '[JobContext Poller - Content Analysis] Starting polling for job ID:',
      contentAnalysisJob.jobId,
    );

    // --- ADDED: Client-side safety timeout ---
    const JOB_TIMEOUT_MS = 19 * 60 * 1000; // 19 minutes, slightly shorter than the backend's 20m

    const intervalId = setInterval(async () => {
      if (!currentUser) {
        console.warn(
          '[JobContext Poller - Content Analysis] User logged out. Stopping.',
        );
        setContentAnalysisJobState({
          jobStatus: 'failed',
          error: { message: 'User session ended.' },
          jobId: null,
        });
        return;
      }

      // --- ADDED: Check against our client-side safety timeout ---
      if (
        contentAnalysisJob.jobStartTime &&
        Date.now() - contentAnalysisJob.jobStartTime > JOB_TIMEOUT_MS
      ) {
        const timeoutError = {
          message: 'The content analysis took too long and timed out.',
          code: 'JOB_TIMEOUT',
        };
        console.error(
          `[JobContext Poller - Content Analysis] Job timed out after ${JOB_TIMEOUT_MS / 60000} minutes.`,
        );
        toast.error(timeoutError.message);
        setContentAnalysisJobState({
          jobStatus: 'failed',
          error: timeoutError,
          jobId: null,
        });
        return;
      }

      try {
        let result = await checkContentAnalysisJobStatus(
          contentAnalysisJob.jobId,
        );

        // --- ADDED: Auth error retry logic ---
        const isAuthError =
          result.statusCode === 401 ||
          result.statusCode === 403 ||
          result.error === 'HTTP_ERROR_401' ||
          result.error === 'HTTP_ERROR_403';

        if (!result.status && isAuthError) {
          console.warn(
            '[JobContext Poller - Content Analysis] Auth error detected. Forcing token refresh and retrying...',
          );
          const auth = getAuth();
          // Ensure there's a current user before trying to refresh
          if (auth.currentUser) {
            await auth.currentUser.getIdToken(true);
            result = await checkContentAnalysisJobStatus(
              contentAnalysisJob.jobId,
            );
          }
        }

        console.log(
          'FRONTEND POLLER: Received API response from /content-analysis-status:',
          result,
        );
        if (result && result.status && result.data) {
          const backendJob = result.data;

          switch (backendJob.status) {
            case 'pending':
              setContentAnalysisJobState({ message: backendJob.message });
              break;

            case 'finished':
              toast.success('Deep content analysis is complete!');
              const jobResults = backendJob.data;
              console.log(
                "[DEBUG 7. Backend Response] Raw 'jobResults' payload from backend:",
                jobResults,
              );

              if (jobResults?.analysis_results) {
                // Create a map of { videoId: market_fit_result }
                const marketFitMap = Object.entries(
                  jobResults.analysis_results,
                ).reduce((acc, [vid, res]) => {
                  acc[vid] = res.market_fit;
                  return acc;
                }, {});
                dispatch({
                  type: actionTypes.SET_INDIVIDUAL_MARKET_FIT_RESULTS,
                  payload: marketFitMap,
                });

                // Create an array of [{ videoId, analysis: content_quality_result }]
                const qualityArray = Object.entries(
                  jobResults.analysis_results,
                ).map(([videoId, res]) => {
                  return {
                    videoId,
                    ...res.content_quality,
                  };
                });
                console.log(
                  "[DEBUG 6. Job Processor] 'qualityArray' being dispatched to dataReducer:",
                  qualityArray,
                );
                dispatch({
                  type: actionTypes.SET_PUBLICATION_AI_SUMMARY,
                  payload: qualityArray,
                });
              }

              // Dispatch aggregated results
              dispatch({
                type: actionTypes.SET_AGGREGATED_ANALYSIS_RESULTS,
                payload: {
                  aggregated_market_fit_analysis:
                    jobResults?.aggregated_market_fit_analysis || null,
                  aggregated_publication_summary:
                    jobResults?.aggregated_publication_summary || null,
                },
              });

              setContentAnalysisJobState({
                jobStatus: 'finished',
                results: jobResults,
                jobId: null,
              });
              break;

            case 'failed':
            case 'error':
              const analysisErrorCode =
                backendJob.error ||
                backendJob.data?.code ||
                backendJob.data?.error;
              const analysisErrorObj = {
                message:
                  backendJob.message ||
                  'Content analysis failed on the server.',
                code: analysisErrorCode,
                data: backendJob.data || null,
              };

              setContentAnalysisJobState({
                jobStatus: 'failed',
                error: analysisErrorObj,
                jobId: null,
              });

              // PROMOTE TO GLOBAL MODAL
              if (
                ['OAUTH_TOKEN_REVOKED', 'OAUTH_NOT_CONFIGURED'].includes(
                  analysisErrorCode,
                )
              ) {
                setGlobalError(analysisErrorObj);
              } else {
                // FIX: Only show generic toast if it's not a critical auth error
                toast.error('The content analysis completed with errors.');
              }
              break;

            default:
              console.warn(
                `[DataContext Poller] Unknown job status: ${backendJob.status}`,
              );
              break;
          }
        } else {
          const communicationErrorObject = {
            message:
              result.message ||
              'Lost connection during analysis. The job may still be running.',
            code: result.error || result.code || 'COMMUNICATION_ERROR', // Use the actual code if available
            data: null,
          };
          console.log(
            'jobContext poller [COMM FAILED]: Setting job context error state to:',
            communicationErrorObject,
          );
          toast.error(communicationErrorObject.message);

          setContentAnalysisJobState({
            jobStatus: 'failed',
            error: communicationErrorObject,
            jobId: null,
          });
        }
      } catch (e) {
        const criticalError = {
          message: 'A critical client-side error occurred while polling.',
          code: 'CRITICAL_POLLER_FAILURE',
          data: null,
        };
        console.error(
          `[JobContext Poller - Content Analysis] Critical exception: ${e.message}`,
        );
        toast.error(criticalError.message);
        setContentAnalysisJobState({
          jobStatus: 'failed',
          error: criticalError,
          jobId: null,
        });
      }
    }, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    contentAnalysisJob.jobId,
    contentAnalysisJob.jobStatus,
    contentAnalysisJob.jobStartTime,
    checkContentAnalysisJobStatus,
    setContentAnalysisJobState,
    currentUser,
    setGlobalError,
    dispatch,
  ]);

  // --- NEW: POLLING LOGIC for AI Agent Page and BillingTab of SuperAdmin Modal ---
  useEffect(() => {
    if (!currentUser) {
      return;
    }

    // Optimization: Only poll if we have jobs that are NOT finished
    const hasActiveJobs = agentJobHistory.some(
      (job) =>
        job.status === 'queued' ||
        job.status === 'running' ||
        job.status === 'pending',
    );

    // 3. Conditional Polling
    if (hasActiveJobs) {
      console.log('[JobContext] Active Agent jobs detected. Polling started.');

      const intervalId = setInterval(() => {
        // This function MUST be wrapped in useCallback in the provider definition
        fetchAgentJobHistory();
      }, 5000);

      // Cleanup: Stop polling when component unmounts OR when hasActiveJobs becomes false
      return () => {
        console.log('[JobContext] Polling stopped.');
        clearInterval(intervalId);
      };
    }
  }, [
    agentJobHistory, // DEP 1: Re-evaluate whenever the list changes
    currentUser, // DEP 3: Re-evaluate on login/logout
    fetchAgentJobHistory, // DEP 4: Stable function reference
  ]);

  // --- Memoized Context Value ---
  const contextValue = useMemo(
    () => ({
      discoveryJob,
      contentAnalysisJob,
      agentJobHistory,
      selectedJobDetails,
      selectedJobType,
      isJobDetailsModalOpen,
      setDiscoveryJobState,
      clearDiscoveryJobState,
      clearDiscoveryJobError,
      setContentAnalysisJobState,
      clearContentAnalysisJobState,
      clearContentAnalysisJobError,
      startDiscoverySearchJob,
      startContentAnalysisJob,
      checkContentAnalysisJobStatus,
      cancelDiscoveryJob,
      cancelContentAnalysisJob,
      updateJobResultsAfterImport, // <-- EXPORT NEW FUNCTION
      updateJobResultsAfterMoveToHold, // <-- EXPORT NEW FUNCTION
      updateJobResultsAfterDiscard, // <-- EXPORT NEW FUNCTION
      startAgentJob,
      fetchAgentJobHistory,
      fetchAgentJobDetails,
      setSelectedJobDetails, // To allow closing the modal from the component
      cancelAgentJob,
      deleteAgentJob,
      sendAgentChat,
      agentChatsList, // Export
      activeAgentChatId, // Export
      getAgentChats, // Export
      setActiveAgentChat, // Export
      addAgentChatToList, // Export
      getAgentChatHistory, // Export
      dispatchMonthlyEmails,
    }),
    [
      discoveryJob,
      contentAnalysisJob,
      agentJobHistory,
      selectedJobDetails,
      selectedJobType,
      isJobDetailsModalOpen,
      setDiscoveryJobState,
      clearDiscoveryJobState,
      clearDiscoveryJobError,
      setContentAnalysisJobState,
      clearContentAnalysisJobState,
      clearContentAnalysisJobError,
      startDiscoverySearchJob,
      startContentAnalysisJob,
      checkContentAnalysisJobStatus,
      cancelDiscoveryJob,
      cancelContentAnalysisJob,
      updateJobResultsAfterImport, // <-- EXPORT NEW FUNCTION
      updateJobResultsAfterMoveToHold, // <-- EXPORT NEW FUNCTION
      updateJobResultsAfterDiscard, // <-- EXPORT NEW FUNCTION
      startAgentJob,
      fetchAgentJobHistory,
      fetchAgentJobDetails,
      setSelectedJobDetails, // To allow closing the modal from the component
      cancelAgentJob,
      deleteAgentJob,
      sendAgentChat,
      agentChatsList, // Export
      activeAgentChatId, // Export
      getAgentChats, // Export
      setActiveAgentChat, // Export
      addAgentChatToList, // Export
      getAgentChatHistory, // Export
      dispatchMonthlyEmails,
    ],
  );

  return (
    <JobContext.Provider value={contextValue}>{children}</JobContext.Provider>
  );
};

// --- Custom Hook for consuming the context ---
export const useJobs = () => {
  const context = useContext(JobContext);
  if (context === undefined) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};
